import { trpc } from '@tg/app-core';
import { useQueryClient } from '@tanstack/react-query';
import { blockCount, fmtLabel, plural3, type WorkoutContent } from '@tg/domain';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { D, MONTHS_N, RU, addDays, dateLong, monday } from '../lib/dates';
import { useMe, useToday } from '../lib/data';
import { setSelDate, useSelDate } from '../lib/nav';
import { lsGet, lsSet, useLocal } from '../lib/storage';
import { blockTitle, visibleBlocks } from '../lib/workout';
import { I, NextIcon } from '../ui/icons';
import { MonthCal, dayStatus } from '../ui/MonthCal';
import { Sheet } from '../ui/overlay';
import { AchFinal, inTelegram, isNative, isStandalone, Mark, openHowto } from '../ui/FirstSteps';
import { RestIcon, Tabs } from '../ui/parts';

/** Сколько дней вперёд держим на устройстве для работы без сети (CLI-3) */
export const OFFLINE_AHEAD = 27;

/** Тренировки недели и вперёд — одним запросом; он же кладётся в оффлайн-кэш */
export function useDaysRange(from: string, to: string) {
  const q = trpc.clientApp.days.useQuery({ from, to }, { staleTime: 60_000 });
  const map = new Map<string, WorkoutContent>();
  for (const d of q.data || []) map.set(d.date, d.content as WorkoutContent);
  return { q, map };
}

/** Тренировка дня: снимок завершённого дня, иначе опубликованная версия */
export function useDay(date: string) {
  const qc = useQueryClient();
  return trpc.clientApp.day.useQuery(
    { date },
    {
      staleTime: 30_000,
      /* Пока грузится (или нет сети) — содержимое из недельного запроса, отметка дня — из календаря:
         завершённый день не должен на время загрузки выглядеть незавершённым */
      placeholderData: () => {
        const cal = qc.getQueriesData<{ date: string; done: boolean }[]>({ queryKey: [['clientApp', 'calendar']] });
        const done = cal.some(([, v]) => v?.some((x) => x.date === date && x.done));
        for (const [, v] of qc.getQueriesData<{ date: string; content: WorkoutContent }[]>({ queryKey: [['clientApp', 'days']] })) {
          const d = v?.find((x) => x.date === date);
          if (d) return { date, content: d.content, frozen: false, done: done ? [''] : [], results: [], messages: [] } as never;
        }
        return undefined;
      },
    },
  );
}

/* H0. Главная: месяц, полоса недели, «Первые шаги», карточка дня */
export function Home() {
  const nav = useNavigate();
  const today = useToday();
  const sel = useSelDate(today);
  const mon = monday(sel);
  const me = useMe();
  useDaysRange(mon, addDays(mon, 6));
  /* Точки недели — статус дня, как в окне месяца: выполнена, пропущена, запланирована */
  const weekCal = trpc.clientApp.calendar.useQuery({ from: mon, to: addDays(mon, 6) });
  const weekSt = new Map((weekCal.data || []).map((x) => [x.date, x]));
  useDaysRange(today, addDays(today, OFFLINE_AHEAD));
  const day = useDay(sel);
  const utils = trpc.useUtils();
  const [hide, setHide] = useLocal('hideTasks', false);
  const [cal, setCal] = useState(false);

  /* Дни недели — заранее: открытая неделя доступна без сети */
  useEffect(() => {
    for (let k = 0; k < 7; k++) utils.clientApp.day.prefetch({ date: addDays(mon, k) });
  }, [mon, utils]);

  const d = D(sel);
  /* Первые шаги — как «Getting started» у HWPO, задачи — из нашего продукта.
     Шаг отмечается по факту и только тем, что клиент сделал сам (сервер: clientApp.me.steps).
     [название, выполнено, кнопка, куда, вкладка профиля]; иконка — только в мобильном вебе,
     в приложении из стора её ставит магазин */
  const s = me.data?.steps;
  const app = isNative();
  const tasks: [string, boolean, string, string, ('pr' | 'meas')?][] = s
    ? [
        ...(app ? [] : [['Добавить иконку на экран', s.pwa || isStandalone(), 'Как?', '@howto'] as [string, boolean, string, string]]),
        ['Внести рекорды', s.pm, 'Внести', '/profile', 'pr'],
        ['Внести замеры', s.meas, 'Внести', '/profile', 'meas'],
        ['Поставить фото профиля', s.photo, 'Поставить', '/profile', 'pr'],
        ['Выполнить первую тренировку', s.workout, 'Начать', '/day/' + today],
      ]
    : [];
  const nDone = tasks.filter((t) => t[1]).length;
  const allDone = !!s && nDone === tasks.length;

  /* Шаги делаются на других экранах (профиль, тренировка) — на главной сверяемся с сервером */
  const pending = !me.data?.ach.start;
  useEffect(() => {
    if (pending) utils.clientApp.me.invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Запуск с иконки записывается в аккаунт — шаг закрыт и в браузере */
  const markPwa = trpc.clientApp.markPwa.useMutation({ onSuccess: () => utils.clientApp.me.invalidate() });
  useEffect(() => {
    if (s && !s.pwa && isStandalone() && !markPwa.isPending) markPwa.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.pwa]);

  /* Все первые шаги пройдены — один раз финал с достижением «Курс молодого бойца» */
  const [ach, setAch] = useState(false);
  const achieve = trpc.clientApp.achieve.useMutation({
    onSuccess: (r) => {
      utils.clientApp.me.setData(undefined, (x) => (x ? { ...x, ach: { ...x.ach, start: r.start } } : x));
      if (r.fresh) setTimeout(() => setAch(true), 250);
    },
  });
  useEffect(() => {
    if (allDone && !me.data?.ach.start && achieve.isIdle) achieve.mutate({ app });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone, me.data?.ach.start]);
  const go = (to: string, tab?: 'pr' | 'meas') => {
    if (to === '@howto') return openHowto();
    /* «Внести» ведёт в профиль сразу на нужную вкладку */
    if (tab) lsSet('ptab', tab);
    nav(to);
  };

  return (
    <>
      <header className="hhead">
        <div className="wmk">Тренерграм</div>
        <button className="mon" onClick={() => setCal(true)} aria-label="Выбрать дату">
          {MONTHS_N[d.getMonth()]} {d.getFullYear()} <I n="cal" />
        </button>
      </header>
      <div className="pg home">
        <section className="card week">
          <button className="ib sm" onClick={() => setSelDate(addDays(sel, -7))} aria-label="Прошлая неделя">
            <I n="left" />
          </button>
          <div className="wds">
            {Array.from({ length: 7 }, (_, k) => {
              const date = addDays(mon, k);
              const st = dayStatus(weekSt.get(date), date, today);
              return (
                <button key={date} className={'wd' + (date === sel ? ' on' : '') + (date === today ? ' today' : '')} onClick={() => setSelDate(date)}>
                  <s>{RU[k]}</s>
                  <b>{D(date).getDate()}</b>
                  <i className={st === 'rest' ? '' : st} />
                </button>
              );
            })}
          </div>
          <button className="ib sm" onClick={() => setSelDate(addDays(sel, 7))} aria-label="Следующая неделя">
            <I n="right" />
          </button>
        </section>
        {me.data && tasks.length && nDone < tasks.length ? (
          <section className="card steps">
            <button className="sth" onClick={() => setHide(!hide)} aria-expanded={!hide}>
              <b>Первые шаги</b>
              <I n="down" cls={hide ? '' : 'up'} />
              <span>
                {nDone} / {tasks.length}
              </span>
            </button>
            <div className="prog">
              <i style={{ width: (nDone / tasks.length) * 100 + '%' }} />
            </div>
            {hide ? null : (
              <div className="tasks">
                {tasks.map(([t, ok, act, to, tab]) => (
                  <div key={t} className={'task' + (ok ? ' ok' : '')}>
                    <i className="ck">{ok ? <I n="check" /> : null}</i>
                    <span>{t}</span>
                    {!ok && act ? (
                      <button className="go" onClick={() => go(to, tab)}>
                        {act} <NextIcon />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}
        <DayCard date={sel} today={today} content={day.data?.content as WorkoutContent | null | undefined} done={!!day.data?.done.includes('')} marks={day.isPlaceholderData ? [] : day.data?.done || []} loading={day.isPending} />
      </div>
      <Tabs on="home" />
      <MonthCal open={cal} sel={sel} today={today} onPick={setSelDate} onClose={() => setCal(false)} />
      <InstallSheet />
      {me.data ? (
        <AchFinal
          id="start"
          open={ach}
          onClose={() => setAch(false)}
          who={{ first: me.data.first, she: me.data.sex === 'f', trainer: me.data.trainer ? [me.data.trainer.first, me.data.trainer.last].filter(Boolean).join(' ') : null }}
        />
      ) : null}
    </>
  );
}

/* Карточка тренировки дня — тёмная, с оранжевым срезом; отдых и соревнование — свои */
function DayCard({ date, today, content, done, marks, loading }: { date: string; today: string; content: WorkoutContent | null | undefined; done: boolean; marks: string[]; loading: boolean }) {
  const nav = useNavigate();
  const when = date === today ? 'Сегодня' : dateLong(date);
  if (loading && content === undefined) return null;
  const blocks = visibleBlocks(content);
  if (!blocks.length && !content?.comp)
    return (
      <section className="card dcard rest">
        <RestIcon />
        <div>
          <s>{when}</s>
          <b>Отдых</b>
          <span>Тренировки нет — восстанавливайтесь</span>
        </div>
      </section>
    );
  if (!blocks.length)
    return (
      <button className="card dcard rest" onClick={() => nav('/day/' + date)}>
        <I n="trophy" />
        <div>
          <s>{when}</s>
          <b>{content!.title}</b>
          <span>Соревнование</span>
        </div>
      </button>
    );
  const n = blocks.reduce((a, b) => a + blockCount(b), 0);
  return (
    <button className={'dcard hero' + (done ? ' done' : '')} onClick={() => nav('/day/' + date)}>
      <i className="cut" />
      <s>
        {when}
        {done ? ' · выполнено' : ''}
      </s>
      <b>{content!.title}</b>
      <span>
        {blocks.length} {plural3(blocks.length, 'блок', 'блока', 'блоков')} · {n} {plural3(n, 'упражнение', 'упражнения', 'упражнений')}
      </span>
      <ol>
        {/* Отметки блоков: выполнен — зелёная галочка; тренировка завершена, а блок нет — красный крестик */}
        {blocks.map((b) => (
          <li key={b.id} className={marks.includes(b.id) ? 'ok' : done ? 'miss' : ''}>
            {marks.includes(b.id) ? <I n="check" /> : done ? <I n="x" /> : null}
            {blockTitle(b)}
            {b.title && b.fmt ? ' · ' + fmtLabel(b.fmt) : ''}
          </li>
        ))}
      </ol>
      <em>
        {done ? (
          <>
            <I n="check" />
            Выполнено
          </>
        ) : (
          'Открыть тренировку'
        )}{' '}
        <NextIcon />
      </em>
    </button>
  );
}

/* Подсказка установки PWA — только в браузере (не установленное приложение,
   не Capacitor), один раз. «Установить»: Android Chrome — системное окно установки
   (beforeinstallprompt), иначе — то же слайдшоу, что у шага «Иконка на экране». */
type BIP = Event & { prompt: () => Promise<void> };
let bip: BIP | null = null;
if (typeof window !== 'undefined')
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    bip = e as BIP;
  });

function InstallSheet() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isStandalone() || isNative() || lsGet('installShown', false)) return;
    lsSet('installShown', true);
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, []);
  const install = async () => {
    setOpen(false);
    if (bip && !inTelegram()) {
      await bip.prompt().catch(() => {});
      bip = null;
    } else setTimeout(openHowto, 240);
  };
  return (
    <Sheet open={open} onClose={() => setOpen(false)} label="Установите Тренерграм на телефон">
      <Mark cls="appic" />
      <h2>Установите Тренерграм на телефон</h2>
      <p>Приложение будет открываться с главного экрана в одно касание и работать без интернета.</p>
      <div className="acts">
        <button className="btn pri" onClick={install}>
          Установить
        </button>
        <button className="btn sec" onClick={() => setOpen(false)}>
          Позже
        </button>
      </div>
    </Sheet>
  );
}
